/* WP-10 bottom sheet / dialog (§14.4.6, §14.6): bottom sheet on phone,
 * centred dialog on tablet/desktop. Focus trap + Escape + backdrop. */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function BottomSheet({
  open,
  onClose,
  title,
  children,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) {
      // Async protected actions may disable their trigger before opening the
      // sheet; remember its focus before the browser moves focus to the body.
      const remember = (e: FocusEvent) => {
        if (e.target instanceof HTMLElement && e.target !== document.body) lastFocused.current = e.target;
      };
      document.addEventListener('focusin', remember);
      return () => document.removeEventListener('focusin', remember);
    }
    if (document.activeElement !== document.body) lastFocused.current = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const focusables = () => [...(el?.querySelectorAll<HTMLElement>(
      'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
    ) ?? [])].filter((n) => !n.hasAttribute('disabled') && n.getClientRects().length > 0);
    (focusables()[0] ?? el)?.focus();
    const root = document.getElementById('root');
    const wasInert = root?.inert ?? false;
    if (root) root.inert = true;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      if (e.key === 'Tab' && el) {
        const list = focusables();
        if (list.length === 0) {
          e.preventDefault();
          el.focus();
          return;
        }
        const firstEl = list[0];
        const lastEl = list[list.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (root) root.inert = wasInert;
      // The protected action re-enables its trigger when cancellation settles.
      // Restore focus after that render rather than while the trigger is disabled.
      const target = lastFocused.current;
      requestAnimationFrame(() => target?.isConnected && target.focus());
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <>
      <div className="hp-sheet-backdrop" onClick={onClose} aria-hidden />
      <div className="hp-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref}>
        <div className="hp-sheet__grab" aria-hidden />
        <h2 className="hp-sheet__title" id={titleId}>{title}</h2>
        {children}
        {actions ? <div className="hp-sheet__actions">{actions}</div> : null}
      </div>
    </>,
    document.body,
  );
}
